/**
 * AdminLayout — practice-aligned shell: gradient sidebar, top bar, page transitions.
 */
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Landmark, LogOut, Menu, Moon, Sun, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gauge,
  Sparkles,
  Users,
  KeyRound,
  Stethoscope,
  ClipboardList,
  HeartPulse,
  Cable,
  Database,
  CircleDollarSign,
  Receipt,
  BarChart3,
  Webhook,
  SlidersHorizontal,
  Building2,
  Wallet,
  BadgeCheck,
  Newspaper,
  CalendarDays,
  CreditCard,
  Pill,
  MapPin,
  BookOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  BrandWordmark,
  HeaderToolButton,
  NotificationBell,
  ShellPageTransition,
  ShellSidebar,
  UserMenu,
  type ShellNavSection,
} from "@/components/layout/shell";
import { useTheme } from "@/providers/theme-provider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { getUserRoleLabel } from "@/components/layout/shell/user-display";

const navSections: ShellNavSection[] = [
  {
    label: "OVERVIEW",
    items: [{ label: "Dashboard", icon: Gauge, href: "/" }],
  },
  {
    label: "MANAGEMENT",
    items: [
      { label: "Organizations", icon: Landmark, href: "/organizations" },
      { label: "Plans", icon: CreditCard, href: "/organizations/plans" },
      { label: "Practice signups", icon: BadgeCheck, href: "/practice-signups" },
      { label: "Community", icon: Newspaper, href: "/community" },
      { label: "Demo Requests", icon: CalendarDays, href: "/demo-requests" },
      { label: "Staff & access", icon: Users, href: "/users" },
      { label: "Roles & Permissions", icon: KeyRound, href: "/roles" },
      { label: "Facilities", icon: Stethoscope, href: "/facilities" },
    ],
  },
  {
    label: "COMPLIANCE",
    items: [{ label: "Audit Logs", icon: ClipboardList, href: "/audit-logs" }],
  },
  {
    label: "SYSTEM",
    items: [
      { label: "System Health", icon: HeartPulse, href: "/system-health" },
      { label: "Integrations", icon: Cable, href: "/integrations" },
      { label: "Code Systems", icon: Database, href: "/code-systems" },
      { label: "ePrescribing Guide", icon: BookOpen, href: "/weno/guide" },
      { label: "WENO Drug DB", icon: Pill, href: "/weno/drug-db" },
      { label: "WENO Pharmacy Directory", icon: MapPin, href: "/weno/pharmacy-directory" },
    ],
  },
  {
    label: "BILLING",
    items: [
      { label: "Overview", icon: CircleDollarSign, href: "/billing" },
      { label: "Accounts", icon: Building2, href: "/billing/accounts" },
      { label: "Invoices", icon: Receipt, href: "/billing/invoices" },
      { label: "Payments", icon: Wallet, href: "/billing/payments" },
      { label: "Billing settings", icon: SlidersHorizontal, href: "/billing/settings" },
    ],
  },
  {
    label: "CONFIGURATION",
    items: [
      { label: "AI Guardrails & Cost", icon: Sparkles, href: "/ai-guardrails" },
      { label: "Clearinghouse & payers", icon: CircleDollarSign, href: "/billing-config" },
      { label: "Billing Guide", icon: Receipt, href: "/billing-guide" },
      { label: "Reports", icon: BarChart3, href: "/reports" },
      { label: "Webhooks", icon: Webhook, href: "/webhooks" },
      { label: "Settings", icon: SlidersHorizontal, href: "/settings" },
    ],
  },
];

function pageBreadcrumb(pathname: string): string {
  if (pathname === "/" || pathname === "") return "Dashboard";
  const segment = pathname.split("/").filter(Boolean)[0] ?? "home";
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
}

function AdminSidebarHeader({ collapsed }: { collapsed: boolean }) {
  return (
    <motion.div
      key={collapsed ? "logo-mini" : "logo-full"}
      initial={{ opacity: 0, x: collapsed ? 0 : -12, scale: collapsed ? 0.85 : 1 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, scale: collapsed ? 0.85 : 1 }}
      transition={{ duration: 0.18 }}
      className="w-full"
    >
      <BrandWordmark collapsed={collapsed} tone="dark" subtitle="EHR Admin Console" />
    </motion.div>
  );
}

function AdminSidebarFooter({
  collapsed,
  onLogout,
}: {
  collapsed: boolean;
  onLogout: () => void;
}) {
  return (
    <div className="shrink-0 border-t border-white/10 bg-black p-2">
      <button
        type="button"
        onClick={onLogout}
        className={cn(
          "flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-semibold text-white/90 transition-colors hover:bg-white/10 hover:text-white",
          collapsed ? "justify-center" : "gap-3",
        )}
        title={collapsed ? "Sign out" : undefined}
      >
        <LogOut className="h-[18px] w-[18px] shrink-0" />
        <AnimatePresence>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              className="whitespace-nowrap"
            >
              Sign out
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
}

function AdminTopBar({ onOpenMobileNav }: { onOpenMobileNav?: () => void }) {
  const location = useLocation();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const roleLabel = getUserRoleLabel(user?.role);
  const breadcrumb = pageBreadcrumb(location.pathname);

  return (
    <header className="relative z-30 flex h-16 shrink-0 items-center justify-between border-b border-border bg-card px-4 backdrop-blur-xl sm:px-8">
      <div className="flex items-center gap-3 min-w-0">
        {onOpenMobileNav && (
          <HeaderToolButton
            variant="default"
            className="lg:hidden bg-card ring-border"
            onClick={onOpenMobileNav}
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </HeaderToolButton>
        )}
        <div className="flex items-center gap-1.5 text-sm min-w-0">
          <span className="font-medium text-muted-foreground shrink-0">Admin</span>
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
          <span className="font-bold text-foreground truncate">{breadcrumb}</span>
        </div>
        <Badge
          variant="outline"
          className="hidden sm:inline-flex shrink-0 text-[10px] font-bold uppercase tracking-wider border-primary/20 bg-primary/8 text-primary"
        >
          {roleLabel}
        </Badge>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger
            className="hidden md:flex items-center gap-2 rounded-2xl border border-border/60 bg-background/80 px-3 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          >
            <Landmark className="h-4 w-4 text-primary/80" />
            <span className="max-w-[140px] truncate">All Organizations</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              Identity Context
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>All Organizations</DropdownMenuItem>
            <DropdownMenuItem>Phoenix Health Systems</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <span className="hidden h-7 w-px bg-border/60 sm:block" aria-hidden />

        <HeaderToolButton
          variant="default"
          className="bg-card text-muted-foreground ring-border hover:bg-muted/50 hover:text-foreground"
          onClick={toggleTheme}
          title={resolvedTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          {resolvedTheme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </HeaderToolButton>

        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const sidebarFooter = (
    <AdminSidebarFooter collapsed={collapsed} onLogout={() => void handleLogout()} />
  );
  const mobileSidebarFooter = (
    <AdminSidebarFooter collapsed={false} onLogout={() => void handleLogout()} />
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      <div className="hidden h-screen shrink-0 lg:block">
        <ShellSidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
          sections={navSections}
          header={<AdminSidebarHeader collapsed={collapsed} />}
          footer={sidebarFooter}
          tone="black"
        />
      </div>

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 h-screen w-[260px] lg:hidden transition-transform duration-300 ease-in-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <ShellSidebar
          collapsed={false}
          onToggle={() => setMobileOpen(false)}
          sections={navSections}
          header={<AdminSidebarHeader collapsed={false} />}
          footer={mobileSidebarFooter}
          tone="black"
        />
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="absolute right-3 top-4 rounded-xl p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <AdminTopBar onOpenMobileNav={() => setMobileOpen(true)} />

        <main className="relative z-0 flex-1 overflow-y-auto overflow-x-hidden">
          <ShellPageTransition className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8">
            <Outlet />
          </ShellPageTransition>
        </main>
      </div>
    </div>
  );
}
